/**
 * PACURecord.js
 *
 * Post-Anaesthesia Care Unit (PACU) record. Layout:
 *   1. Context strip — procedure + anaesthesia type
 *   2. Active entry form — single vitals + Aldrete card
 *   3. Vitals log — compact summary table of saved entries
 *   4. Discharge section — destination + ward/bed, gated by Aldrete >= 9
 *
 * Props:
 *   surgeryId       - unique surgery identifier
 *   procedure       - procedure name (context display)
 *   anaesthesiaType - anaesthesia type key (context display)
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Snackbar from '@material-ui/core/Snackbar';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import axios from 'axios';
import { config } from '../../../Helpers/env';
import { CustomSelect } from '../../Utils/FormInputs/SelectInput';
import { surgeryService } from '../../../services/surgeryService';
import { ALDRETE_DISCHARGE_THRESHOLD, ANAESTHESIA_TYPE_LABELS } from '../shared/constants';
import PACUVitalsEntry, { ALDRETE_CRITERIA } from './PACUVitalsEntry';

const ALDRETE_KEYS = ALDRETE_CRITERIA.map((c) => c.key);

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
  },
  // ── Inline score badge (top-right) ────────────────
  scoreBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderRadius: 8,
    flexShrink: 0,
  },
  scoreBadgeLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  scoreBadgeValue: {
    fontSize: '1.2rem',
    fontWeight: 800,
    fontFamily: 'monospace',
  },
  scoreRed: { backgroundColor: '#FFEBEE', color: '#C62828' },
  scoreAmber: { backgroundColor: '#FFF8E1', color: '#F57F17' },
  scoreGreen: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  scoreNeutral: { backgroundColor: '#F5F5F5', color: '#9E9E9E' },
  // ── Context strip ─────────────────────────────────
  contextStrip: {
    display: 'flex',
    gap: 24,
    padding: '10px 16px',
    backgroundColor: '#FAFAFA',
    borderRadius: 6,
    marginBottom: 20,
    border: '1px solid #EEEEEE',
  },
  contextItem: {
    fontSize: '0.82rem',
    color: '#424242',
  },
  contextLabel: {
    color: '#9E9E9E',
    marginRight: 6,
  },
  // ── Sections ──────────────────────────────────────
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  section: {
    marginBottom: 24,
  },
  activeEntryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  entryActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 12,
  },
  // ── History table ─────────────────────────────────
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.82rem',
  },
  historyTh: {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#616161',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '2px solid #E0E0E0',
    backgroundColor: '#FAFAFA',
  },
  historyTd: {
    padding: '10px 12px',
    borderBottom: '1px solid #F0F0F0',
    color: '#424242',
  },
  historyTr: {
    '&:hover': { backgroundColor: '#FAFAFA' },
  },
  scorePill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '0.78rem',
  },
  pillRed: { backgroundColor: '#FFEBEE', color: '#C62828' },
  pillAmber: { backgroundColor: '#FFF8E1', color: '#F57F17' },
  pillGreen: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  emptyLog: {
    textAlign: 'center',
    padding: '24px 0',
    color: '#BDBDBD',
    fontSize: '0.82rem',
    fontStyle: 'italic',
  },
  // ── Discharge bar ──────────────────────────────────
  dischargeBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #EEEEEE',
    marginTop: 24,
  },
  dischargeReady: {
    borderColor: '#66BB6A',
    backgroundColor: '#FAFFF9',
  },
  dischargeNotReady: {
    opacity: 0.6,
    backgroundColor: '#FAFAFA',
  },
  dischargeLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginRight: 4,
  },
  dischargeField: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  dischargeFieldLabel: {
    fontSize: '0.78rem',
    color: '#757575',
    whiteSpace: 'nowrap',
  },
  dischargeSelect: {
    width: 160,
  },
  dischargeSpacer: {
    flex: 1,
  },
  dischargeNote: {
    fontSize: '0.75rem',
    color: '#9E9E9E',
    fontStyle: 'italic',
  },
  dischargedBanner: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 20,
    padding: '14px 18px',
    borderRadius: 8,
    border: '1px solid #66BB6A',
    backgroundColor: '#F1F8E9',
    marginTop: 24,
  },
  dischargedTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#2E7D32',
  },
  dischargedDetail: {
    fontSize: '0.78rem',
    color: '#424242',
  },
  dischargedDetailLabel: {
    color: '#757575',
    marginRight: 4,
  },
  aldreteTooltip: {
    backgroundColor: '#fff',
    color: '#424242',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    padding: '10px 14px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: 260,
  },
  aldreteTooltipArrow: {
    color: '#fff',
    '&::before': {
      border: '1px solid #E0E0E0',
    },
  },
}));

const toLocalDatetimeStr = (date) => {
  const d = date || new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatShortDateTime = (dtStr) => {
  if (!dtStr) return '--';
  const d = new Date(dtStr);
  if (isNaN(d)) return dtStr;
  return d.toLocaleString('en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

const createEmptyEntry = () => ({
  id: Date.now() + Math.random(),
  recordedAt: toLocalDatetimeStr(new Date()),
  systolic: '',
  diastolic: '',
  heartRate: '',
  spo2: '',
  temperature: '',
  respiratoryRate: '',
  activity: null,
  respiration: null,
  circulation: null,
  consciousness: null,
  o2Saturation: null,
});

const calcAldrete = (entry) => {
  let sum = 0;
  let allScored = true;
  ALDRETE_KEYS.forEach((key) => {
    const val = entry[key];
    if (val !== undefined && val !== null && val !== '') {
      sum += Number(val);
    } else {
      allScored = false;
    }
  });
  return { sum, allScored };
};

/** Convert an API response entry back to the local state shape */
const apiEntryToLocal = (entry) => {
  const v = entry.vitals || {};
  const a = entry.aldrete || {};
  return {
    id: entry.pacu_vitals_id || Date.now() + Math.random(),
    recordedAt: entry.recorded_at ? entry.recorded_at.replace('T', 'T').slice(0, 16) : '',
    systolic: v.systolic || '',
    diastolic: v.diastolic || '',
    heartRate: v.heartRate || '',
    spo2: v.spo2 || '',
    temperature: v.temperature || '',
    respiratoryRate: v.respiratoryRate || '',
    activity: a.activity ?? null,
    respiration: a.respiration ?? null,
    circulation: a.circulation ?? null,
    consciousness: a.consciousness ?? null,
    o2Saturation: a.o2Saturation ?? null,
  };
};

const PACURecord = ({ surgeryId, procedure, anaesthesiaType }) => {
  const classes = useStyles();

  const [savedEntries, setSavedEntries] = useState([]);
  const [activeEntry, setActiveEntry] = useState(createEmptyEntry());
  const [destination, setDestination] = useState('ward');
  const [wardOptions, setWardOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [selectedWard, setSelectedWard] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [discharged, setDischarged] = useState(null); // null = not discharged, object = discharge info
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Load existing vitals + discharge status on mount
  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.getPACUVitals({ surgery_id: surgeryId }).then((res) => {
      const data = res.data || res;
      const entries = (data.entries || []).map(apiEntryToLocal);
      if (entries.length > 0) setSavedEntries(entries);
      if (data.discharge) setDischarged(data.discharge);
    }).catch(() => {});
  }, [surgeryId]);

  // Fetch wards on mount
  useEffect(() => {
    axios.get(`${config.smsUrl}/cbedmanager/getAllWards`).then((res) => {
      const data = res.data || {};
      const list = data.wards || [];
      setWardOptions(list.map((wd) => ({ label: wd.name, value: wd.ward_id })));
    }).catch(() => {});
  }, []);

  const handleWardChange = (option) => {
    setSelectedWard(option);
    setSelectedRoom(null);
    setSelectedBed(null);
    setRoomOptions([]);
    setBedOptions([]);
    if (!option) return;
    const formData = new FormData();
    formData.append('ward_id', option.value);
    axios.post(`${config.smsUrl}/cbedmanager/retrieve_room_by_ward_id`, formData).then((res) => {
      const data = res.data || {};
      const list = data.room || [];
      setRoomOptions(list.map((rm) => ({ label: rm.name, value: rm.room_id })));
    }).catch(() => {});
  };

  const handleRoomChange = (option) => {
    setSelectedRoom(option);
    setSelectedBed(null);
    setBedOptions([]);
    if (!option) return;
    const formData = new FormData();
    formData.append('room_id', option.value);
    axios.post(`${config.smsUrl}/cbedmanager/retrieve_bed_by_room_id`, formData).then((res) => {
      const data = res.data || {};
      const list = data.bed || [];
      setBedOptions(list.map((bd) => ({ label: bd.bed_number, value: bd.bed_id })));
    }).catch(() => {});
  };

  const handleBedChange = (option) => {
    setSelectedBed(option);
  };

  const activeAldrete = useMemo(() => calcAldrete(activeEntry), [activeEntry]);
  const latestAldrete = useMemo(() => {
    if (activeAldrete.allScored) return activeAldrete;
    if (savedEntries.length > 0) return calcAldrete(savedEntries[savedEntries.length - 1]);
    return activeAldrete;
  }, [activeAldrete, savedEntries]);

  const canDischarge = latestAldrete.allScored && latestAldrete.sum >= ALDRETE_DISCHARGE_THRESHOLD;

  // ── Handlers ───────────────────────────────────────
  const handleActiveChange = useCallback((field, value) => {
    setActiveEntry((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveEntry = async () => {
    setLoading(true);
    try {
      const aldrete = calcAldrete(activeEntry);
      const res = await surgeryService.addPACUVitals({
        surgery_id: surgeryId,
        recorded_at: activeEntry.recordedAt,
        vitals: {
          systolic: activeEntry.systolic,
          diastolic: activeEntry.diastolic,
          heartRate: activeEntry.heartRate,
          spo2: activeEntry.spo2,
          temperature: activeEntry.temperature,
          respiratoryRate: activeEntry.respiratoryRate,
        },
        aldrete: {
          activity: activeEntry.activity,
          respiration: activeEntry.respiration,
          circulation: activeEntry.circulation,
          consciousness: activeEntry.consciousness,
          o2Saturation: activeEntry.o2Saturation,
        },
        aldrete_total: aldrete.sum,
      });
      const data = res.data || res;
      const entries = (data.entries || []).map(apiEntryToLocal);
      setSavedEntries(entries);
      setActiveEntry(createEmptyEntry());
      showSnackbar('Vitals saved');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save vitals', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDischarge = async () => {
    if (!canDischarge) return;
    setLoading(true);
    try {
      const res = await surgeryService.dischargePACU({
        surgery_id: surgeryId,
        destination,
        ward_id: selectedWard ? selectedWard.value : '',
        ward: selectedWard ? selectedWard.label : '',
        room_id: selectedRoom ? selectedRoom.value : '',
        room: selectedRoom ? selectedRoom.label : '',
        bed_id: selectedBed ? selectedBed.value : '',
        bed: selectedBed ? selectedBed.label : '',
        aldrete_score: latestAldrete.sum,
        discharge_time: new Date().toISOString(),
      });
      const data = res.data || res;
      setDischarged({
        discharged_at: new Date().toISOString(),
        destination,
        ward: selectedWard ? selectedWard.label : '',
        room: selectedRoom ? selectedRoom.label : '',
        bed: selectedBed ? selectedBed.label : '',
        aldrete_score: latestAldrete.sum,
      });
      showSnackbar(data.message || 'Patient discharged from PACU');
    } catch (err) {
      showSnackbar(err.message || 'Failed to discharge from PACU', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Score helpers ──────────────────────────────────
  const getScoreBadgeClass = (score, allScored) => {
    if (!allScored) return classes.scoreNeutral;
    if (score < 7) return classes.scoreRed;
    if (score < 9) return classes.scoreAmber;
    return classes.scoreGreen;
  };

  const getPillClass = (score) => {
    if (score < 7) return classes.pillRed;
    if (score < 9) return classes.pillAmber;
    return classes.pillGreen;
  };

  const anaesthesiaLabel = ANAESTHESIA_TYPE_LABELS[anaesthesiaType] || anaesthesiaType || '--';

  const renderAldreteTooltip = (entry) => {
    const getScoreColor = (val) => {
      if (val === null || val === undefined) return '#9E9E9E';
      if (val === 2) return '#2E7D32';
      if (val === 1) return '#F57F17';
      return '#C62828';
    };
    return (
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#9E9E9E', marginBottom: 6 }}>
          Aldrete Breakdown
        </div>
        {ALDRETE_CRITERIA.map((c) => {
          const val = entry[c.key];
          const scored = val !== null && val !== undefined;
          return (
            <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: '0.78rem' }}>
              <span style={{ color: '#616161' }}>{c.label}</span>
              <span style={{ fontWeight: 600, color: getScoreColor(val), minWidth: 20, textAlign: 'right' }}>
                {scored ? `${val}/2` : '--'}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Paper className={classes.root} elevation={1}>
      {/* ── Header + inline score ──────────────────── */}
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>PACU Record</Typography>
          <Typography className={classes.subtitle}>Post-Anaesthesia Care Unit</Typography>
        </div>
        <Tooltip
          arrow
          interactive
          placement="bottom-end"
          classes={{ tooltip: classes.aldreteTooltip, arrow: classes.aldreteTooltipArrow }}
          title={latestAldrete.allScored
            ? renderAldreteTooltip(activeAldrete.allScored ? activeEntry : savedEntries[savedEntries.length - 1])
            : 'No Aldrete scores recorded yet'
          }
        >
          <div className={`${classes.scoreBadge} ${getScoreBadgeClass(latestAldrete.sum, latestAldrete.allScored)}`} style={{ cursor: 'pointer' }}>
            <span className={classes.scoreBadgeLabel}>Aldrete</span>
            <span className={classes.scoreBadgeValue}>
              {latestAldrete.allScored ? `${latestAldrete.sum}/10` : '--'}
            </span>
          </div>
        </Tooltip>
      </div>

      {/* ── Context strip ──────────────────────────── */}
      <div className={classes.contextStrip}>
        <span className={classes.contextItem}>
          <span className={classes.contextLabel}>Procedure:</span>
          {procedure || '--'}
        </span>
        <span className={classes.contextItem}>
          <span className={classes.contextLabel}>Anaesthesia:</span>
          {anaesthesiaLabel}
        </span>
      </div>

      {/* ── Active Vitals Entry (hidden after discharge) ── */}
      {!discharged && (
      <div className={classes.section}>
        <div className={classes.activeEntryHeader}>
          <Typography className={classes.sectionTitle} style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Record Vitals
          </Typography>
          <span style={{ fontSize: '0.75rem', color: '#9E9E9E' }}>
            Recommended every 15 min
          </span>
        </div>

        <PACUVitalsEntry
          values={activeEntry}
          onChange={handleActiveChange}
          disabled={loading}
        />

        <div className={classes.entryActions}>
          <button
            type="button"
            className="btn btn-sm sms-info-btn px-4"
            onClick={(e) => { e.stopPropagation(); handleSaveEntry(); }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
      )}

      {/* ── Vitals Log ─────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Vitals Log</Typography>

        {savedEntries.length === 0 ? (
          <div className={classes.emptyLog}>
            No entries recorded yet. Fill in the vitals above and save.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={classes.historyTable}>
              <thead>
                <tr>
                  <th className={classes.historyTh}>#</th>
                  <th className={classes.historyTh}>Date / Time</th>
                  <th className={classes.historyTh}>BP</th>
                  <th className={classes.historyTh}>HR</th>
                  <th className={classes.historyTh}>SpO2</th>
                  <th className={classes.historyTh}>Temp</th>
                  <th className={classes.historyTh}>RR</th>
                  <th className={classes.historyTh}>Aldrete</th>
                </tr>
              </thead>
              <tbody>
                {savedEntries.map((entry, idx) => {
                  const score = calcAldrete(entry);
                  return (
                    <tr key={entry.id} className={classes.historyTr}>
                      <td className={classes.historyTd} style={{ color: '#9E9E9E' }}>{idx + 1}</td>
                      <td className={classes.historyTd} style={{ fontWeight: 500 }}>{formatShortDateTime(entry.recordedAt)}</td>
                      <td className={classes.historyTd}>
                        {entry.systolic && entry.diastolic ? `${entry.systolic}/${entry.diastolic}` : '--'}
                      </td>
                      <td className={classes.historyTd}>{entry.heartRate || '--'}</td>
                      <td className={classes.historyTd}>{entry.spo2 ? `${entry.spo2}%` : '--'}</td>
                      <td className={classes.historyTd}>{entry.temperature ? `${entry.temperature}°C` : '--'}</td>
                      <td className={classes.historyTd}>{entry.respiratoryRate || '--'}</td>
                      <td className={classes.historyTd}>
                        <Tooltip
                          arrow
                          interactive
                          placement="left"
                          classes={{ tooltip: classes.aldreteTooltip, arrow: classes.aldreteTooltipArrow }}
                          title={renderAldreteTooltip(entry)}
                        >
                          <span className={`${classes.scorePill} ${getPillClass(score.sum)}`} style={{ cursor: 'pointer' }}>
                            {score.sum}/10
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Discharged banner (read-only) ── */}
      {discharged && (
        <div className={classes.dischargedBanner}>
          <span className={classes.dischargedTitle}>Discharged</span>
          <span className={classes.dischargedDetail}>
            <span className={classes.dischargedDetailLabel}>Destination:</span>
            {discharged.destination === 'icu' ? 'ICU' : 'Ward'}
          </span>
          {discharged.ward && (
            <span className={classes.dischargedDetail}>
              <span className={classes.dischargedDetailLabel}>Ward:</span>
              {discharged.ward}
            </span>
          )}
          {discharged.room && (
            <span className={classes.dischargedDetail}>
              <span className={classes.dischargedDetailLabel}>Room:</span>
              {discharged.room}
            </span>
          )}
          {discharged.bed && (
            <span className={classes.dischargedDetail}>
              <span className={classes.dischargedDetailLabel}>Bed:</span>
              {discharged.bed}
            </span>
          )}
          {discharged.discharged_at && (
            <span className={classes.dischargedDetail}>
              <span className={classes.dischargedDetailLabel}>Time:</span>
              {formatShortDateTime(discharged.discharged_at)}
            </span>
          )}
        </div>
      )}

      {/* ── Discharge bar — only visible before discharge ── */}
      {!discharged && savedEntries.length > 0 && (
      <div className={`${classes.dischargeBar} ${canDischarge ? classes.dischargeReady : classes.dischargeNotReady}`}>
        <span className={classes.dischargeLabel}>Discharge</span>

        <FormControl component="fieldset" size="small">
          <RadioGroup row value={destination} onChange={(e) => {
            setDestination(e.target.value);
            setSelectedWard(null);
            setSelectedRoom(null);
            setSelectedBed(null);
            setRoomOptions([]);
            setBedOptions([]);
          }}>
            <FormControlLabel value="ward" control={<Radio size="small" color="primary" />} label={<span style={{ fontSize: '0.78rem' }}>Ward</span>} />
            <FormControlLabel value="icu" control={<Radio size="small" color="primary" />} label={<span style={{ fontSize: '0.78rem' }}>ICU</span>} />
          </RadioGroup>
        </FormControl>

        <div className={classes.dischargeField}>
          <span className={classes.dischargeFieldLabel}>Ward:</span>
          <div style={{ width: 160 }}>
            <CustomSelect
              value={selectedWard}
              onChange={handleWardChange}
              options={wardOptions}
              placeholder="Select ward..."
              isDisabled={loading || !canDischarge}
            />
          </div>
        </div>

        <div className={classes.dischargeField}>
          <span className={classes.dischargeFieldLabel}>Room:</span>
          <div style={{ width: 160 }}>
            <CustomSelect
              value={selectedRoom}
              onChange={handleRoomChange}
              options={roomOptions}
              placeholder="Select room..."
              isDisabled={loading || !canDischarge || !selectedWard}
            />
          </div>
        </div>

        <div className={classes.dischargeField}>
          <span className={classes.dischargeFieldLabel}>Bed:</span>
          <div style={{ width: 160 }}>
            <CustomSelect
              value={selectedBed}
              onChange={handleBedChange}
              options={bedOptions}
              placeholder="Select bed..."
              isDisabled={loading || !canDischarge || !selectedRoom}
            />
          </div>
        </div>

        <div className={classes.dischargeSpacer} />

        {!canDischarge && (
          <span className={classes.dischargeNote}>
            Aldrete must be &ge; {ALDRETE_DISCHARGE_THRESHOLD}
          </span>
        )}

        <button
          type="button"
          className={`btn btn-sm px-3 ${canDischarge ? 'sms-btn' : 'sms-gray-btn'}`}
          onClick={(e) => { e.stopPropagation(); handleDischarge(); }}
          disabled={!canDischarge || loading}
        >
          {loading ? 'Processing...' : 'Discharge'}
        </button>
      </div>
      )}

      {/* ── Snackbar ───────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PACURecord;
